import {
  Component,
  LOCALE_ID,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import { addDays, addMinutes, isSameDay, startOfDay } from 'date-fns';

import { CalendarModeType } from '../../../types/calendar';
import { CamCalendarEngine } from '../../calendar-engine';
import { CamRenderedContentDirective } from '../../directives/rendered-content.directive';
import {
  MS_PER_HOUR,
  addUnit,
  clamp,
  eachDay,
  monthGridRange,
  overlaps,
  snapToMinutes,
  startOfWeekMonday,
  toDate,
} from '../../helpers/date';
import { CamDragHelper } from '../../helpers/drag';
import { computeLanes } from '../../helpers/layout';
import { expandRecurrence } from '../../helpers/recurrence';
import { CamEventRecord } from '../../models/event-record';
import { CamRecord } from '../../models/record';
import {
  CamCalendarModeConfig,
  CamCalendarModesConfig,
  CamEventConfig,
  CamEventRenderer,
  CamExternalEventSourceConfig,
  CamResourceConfig,
  CamTimeRangeConfig,
} from '../../types';

export type CamCalendarEventBox = {
  cls: string;
  color: string | null;
  content: HTMLElement | string | null;
  height: number;
  key: string;
  left: number;
  record: CamEventRecord;
  top: number;
  width: number;
};

export type CamCalendarTimeRangeBox = {
  cls: string;
  height: number;
  key: string;
  name: string;
  top: number;
};

export type CamCalendarDayColumn = {
  allDayEvents: CamCalendarEventBox[];
  date: Date;
  events: CamCalendarEventBox[];
  iso: string;
  isToday: boolean;
  key: string;
  label: string;
  timeRanges: CamCalendarTimeRangeBox[];
  weekday: string;
};

export type CamCalendarMonthCell = {
  date: Date;
  events: CamCalendarEventBox[];
  iso: string;
  isOtherMonth: boolean;
  isToday: boolean;
  key: string;
  number: number;
};

export type CamCalendarYearCell = {
  date: Date;
  hasEvents: boolean;
  isOtherMonth: boolean;
  isToday: boolean;
  key: string;
  number: number;
};

export type CamCalendarYearMonth = {
  cells: CamCalendarYearCell[];
  key: string;
  label: string;
};

type CamDragState = {
  kind: 'move' | 'resize';
  originEnd: Date;
  originStart: Date;
  pointerY: number;
  record: CamEventRecord;
};

const DEFAULT_DAY_START = 0;
const DEFAULT_DAY_END = 24;
const DEFAULT_HOUR_HEIGHT = 60;
const SNAP_MINUTES = 15;
const MIN_EVENT_MINUTES = 15;
const MS_PER_DAY = 24 * MS_PER_HOUR;

@Component({
  imports: [CamRenderedContentDirective],
  selector: 'cam-calendar-widget',
  styleUrl: './calendar-widget.component.scss',
  templateUrl: './calendar-widget.component.html',
})
export class CamCalendarWidgetComponent implements OnDestroy {
  date = input<Date>(new Date());

  mode = input<CalendarModeType>('week');

  modes = input<CamCalendarModesConfig>({});

  events = input<CamEventConfig[]>([]);

  resources = input<CamResourceConfig[]>([]);

  timeRanges = input<CamTimeRangeConfig[]>([]);

  timeRangesFeature = input<{ headerWidth?: number } | boolean>(false);

  externalEventSourceFeature = input<CamExternalEventSourceConfig | boolean>(false);

  eventRenderer = input<CamEventRenderer | null>(null);

  beforeDragMoveEnd = output<{ eventRecord: CamEventRecord; newEndDate: Date; newStartDate: Date }>();

  beforeDragResizeEnd = output<{ eventRecord: CamEventRecord; newEndDate: Date; newStartDate: Date }>();

  readonly instance = new CamCalendarEngine();

  readonly activeMode = computed<CalendarModeType>(() => this.instance.currentMode());

  readonly hourHeight = computed(() => this._modeConfig().hourHeight ?? DEFAULT_HOUR_HEIGHT);

  readonly dayStartTime = computed(() => this._modeConfig().dayStartTime ?? DEFAULT_DAY_START);

  readonly dayEndTime = computed(() => this._modeConfig().dayEndTime ?? DEFAULT_DAY_END);

  readonly timeRangeHeaderWidth = computed(() => {
    const feature = this.timeRangesFeature();
    if (!feature || feature === true) {
      return 0;
    }
    return feature.headerWidth ?? 0;
  });

  readonly hours = computed(() => {
    const start = this.dayStartTime();
    const end = this.dayEndTime();
    return Array.from({ length: Math.max(1, end - start) }, (_, index) => start + index);
  });

  readonly hourLabels = computed(() =>
    this.hours().map(hour => ({ hour, label: this._hourFormatter.format(new Date(2000, 0, 1, hour, 0)) }))
  );

  readonly gridHeight = computed(() => this.hours().length * this.hourHeight());

  readonly hasAllDayRow = computed(() => this.dayColumns().some(column => column.allDayEvents.length > 0));

  readonly dayColumns = computed<CamCalendarDayColumn[]>(() => {
    const mode = this.activeMode();
    if (mode !== 'day' && mode !== 'week') {
      return [];
    }
    const range = this.instance.getRange(mode, this.instance.currentDate());
    return eachDay(range.startDate, range.endDate).map(day => this._buildDayColumn(day));
  });

  readonly weekdayLabels = computed(() => {
    const monday = startOfWeekMonday(new Date());
    return Array.from({ length: 7 }, (_, index) => this._weekdayFormatter.format(addDays(monday, index)));
  });

  readonly monthWeeks = computed<CamCalendarMonthCell[][]>(() => {
    if (this.activeMode() !== 'month') {
      return [];
    }
    const grid = monthGridRange(this.instance.currentDate());
    const currentMonth = this.instance.currentDate().getMonth();
    const days = eachDay(grid.start, grid.end);
    const spans = this._eventSpans();

    return Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) =>
      days.slice(weekIndex * 7, weekIndex * 7 + 7).map(day => ({
        date: day,
        events: this._buildDayChips(day, spans),
        iso: startOfDay(day).toISOString(),
        isOtherMonth: day.getMonth() !== currentMonth,
        isToday: isSameDay(day, new Date()),
        key: day.toISOString(),
        number: day.getDate(),
      }))
    );
  });

  readonly yearMonths = computed<CamCalendarYearMonth[]>(() => {
    if (this.activeMode() !== 'year') {
      return [];
    }
    const year = this.instance.currentDate().getFullYear();
    const busyDays = new Set(this._eventRecords().map(record => startOfDay(record.getStartDate()).getTime()));

    return Array.from({ length: 12 }, (_, month) => {
      const reference = new Date(year, month, 1);
      const grid = monthGridRange(reference);
      return {
        cells: eachDay(grid.start, grid.end).map(day => ({
          date: day,
          hasEvents: busyDays.has(startOfDay(day).getTime()),
          isOtherMonth: day.getMonth() !== month,
          isToday: isSameDay(day, new Date()),
          key: day.toISOString(),
          number: day.getDate(),
        })),
        key: `${year}-${month}`,
        label: this._monthFormatter.format(reference),
      };
    });
  });

  readonly nowIndicator = computed(() => {
    this.instance.revision();
    const now = new Date();
    const start = this.dayStartTime();
    const minutes = now.getHours() * 60 + now.getMinutes() - start * 60;
    if (minutes < 0 || minutes > (this.dayEndTime() - start) * 60) {
      return null;
    }
    return (minutes / 60) * this.hourHeight();
  });

  private readonly _locale = inject(LOCALE_ID);

  private readonly _dayFormatter = new Intl.DateTimeFormat(this._locale, { day: 'numeric', month: 'short' });
  private readonly _weekdayFormatter = new Intl.DateTimeFormat(this._locale, { weekday: 'short' });
  private readonly _monthFormatter = new Intl.DateTimeFormat(this._locale, { month: 'long' });
  private readonly _hourFormatter = new Intl.DateTimeFormat(this._locale, { hour: '2-digit', minute: '2-digit' });

  private readonly _dragPreview = signal<{ end: Date; record: CamEventRecord; start: Date } | null>(null);
  private readonly _resourcesById = computed(() => {
    this.instance.revision();
    return new Map(this.instance.resourceStore.records.map(resource => [resource.id, resource]));
  });
  private readonly _contentCache = new Map<string, HTMLElement | string>();

  private _dragState: CamDragState | null = null;
  private _externalDrag: CamDragHelper | null = null;

  constructor() {
    effect(() => {
      const date = this.date();
      untracked(() => this.instance.setDate(date));
    });
    effect(() => {
      const mode = this.mode();
      untracked(() => {
        this.instance.setMode(mode);
      });
    });
    effect(() => {
      const configs = this.events();
      untracked(() => {
        this._contentCache.clear();
        this.instance.eventStore.setRecords(configs.map(config => new CamEventRecord(config)));
      });
    });
    effect(() => {
      const configs = this.resources();
      untracked(() => this.instance.resourceStore.setRecords(configs.map(config => new CamRecord(config))));
    });
    effect(() => {
      const configs = this.timeRanges();
      untracked(() => this.instance.timeRangeStore.setRecords(configs.map(config => new CamRecord(config))));
    });
    effect(() => {
      const feature = this.externalEventSourceFeature();
      untracked(() => this._setupExternalDrag(feature));
    });
  }

  ngOnDestroy(): void {
    this._externalDrag?.destroy();
    this._externalDrag = null;
    this._stopDrag();
  }

  public selectDay(date: Date): void {
    this.instance.setDate(date);
    this.instance.setMode('day');
  }

  public onEventPointerDown(event: PointerEvent, box: CamCalendarEventBox, kind: 'move' | 'resize'): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this._dragState = {
      kind,
      originEnd: box.record.getEndDate(),
      originStart: box.record.getStartDate(),
      pointerY: event.clientY,
      record: box.record,
    };
    document.addEventListener('pointermove', this._onDragMove);
    document.addEventListener('pointerup', this._onDragEnd);
  }

  private _modeConfig(): CamCalendarModeConfig {
    return this.modes()[this.activeMode()] ?? {};
  }

  private _eventSpans(): { end: Date; record: CamEventRecord; start: Date }[] {
    return this._eventRecords().map(record => ({ record, ...this._resolveDates(record) }));
  }

  private _eventRecords(): CamEventRecord[] {
    this.instance.revision();
    return this.instance.eventStore.records;
  }

  private _resolveDates(record: CamEventRecord): { end: Date; start: Date } {
    const preview = this._dragPreview();
    if (preview && preview.record === record) {
      return { end: preview.end, start: preview.start };
    }
    return { end: record.getEndDate(), start: record.getStartDate() };
  }

  private _resourceColor(record: CamEventRecord): string | null {
    if (record.data['eventColor']) {
      return record.data['eventColor'];
    }
    const resourceId = record.getResourceId();
    if (resourceId === null || resourceId === undefined) {
      return null;
    }
    return this._resourcesById().get(resourceId)?.getData('eventColor') ?? null;
  }

  private _content(record: CamEventRecord, key: string): HTMLElement | string | null {
    const cached = this._contentCache.get(key);
    if (cached) {
      return cached;
    }
    const renderer = this.eventRenderer();
    if (!renderer) {
      return record.getName();
    }
    const rendered = renderer({ eventRecord: record, resourceRecord: null });
    this._contentCache.set(key, rendered);
    return rendered;
  }

  private _toBox(record: CamEventRecord, key: string): CamCalendarEventBox {
    return {
      cls: record.data['cls'] ?? '',
      color: this._resourceColor(record),
      content: this._content(record, key),
      height: 0,
      key,
      left: 0,
      record,
      top: 0,
      width: 100,
    };
  }

  private _buildDayColumn(day: Date): CamCalendarDayColumn {
    const dayStart = startOfDay(day);
    const windowStart = addMinutes(dayStart, this.dayStartTime() * 60);
    const windowEnd = addMinutes(dayStart, this.dayEndTime() * 60);
    const hourHeight = this.hourHeight();

    const timed: { end: Date; record: CamEventRecord; start: Date }[] = [];
    const allDay: CamEventRecord[] = [];

    this._eventRecords().forEach(record => {
      const { end, start } = this._resolveDates(record);
      if (!overlaps(start, end, dayStart, addDays(dayStart, 1))) {
        return;
      }
      if (record.isAllDay() || end.getTime() - start.getTime() >= MS_PER_DAY) {
        allDay.push(record);
        return;
      }
      timed.push({ end, record, start });
    });

    const layoutItems = timed.map(item => ({
      end: clamp(item.end, windowStart, windowEnd).getTime(),
      record: item.record,
      start: clamp(item.start, windowStart, windowEnd).getTime(),
    }));
    const lanes = computeLanes(layoutItems);

    const events = layoutItems.map(item => {
      const slot = lanes.get(item) ?? { lane: 0, lanes: 1 };
      return {
        ...this._toBox(item.record, `${item.record.id ?? item.record.getName()}-${dayStart.toISOString()}`),
        height: Math.max(18, ((item.end - item.start) / MS_PER_HOUR) * hourHeight),
        left: (slot.lane / slot.lanes) * 100,
        top: ((item.start - windowStart.getTime()) / MS_PER_HOUR) * hourHeight,
        width: 100 / slot.lanes,
      };
    });

    return {
      allDayEvents: allDay.map(record =>
        this._toBox(record, `allday-${record.id ?? record.getName()}-${dayStart.toISOString()}`)
      ),
      date: day,
      events,
      iso: dayStart.toISOString(),
      isToday: isSameDay(day, new Date()),
      key: dayStart.toISOString(),
      label: this._dayFormatter.format(day),
      timeRanges: this._buildTimeRanges(dayStart, windowStart, windowEnd),
      weekday: this._weekdayFormatter.format(day),
    };
  }

  private _buildTimeRanges(dayStart: Date, windowStart: Date, windowEnd: Date): CamCalendarTimeRangeBox[] {
    const hourHeight = this.hourHeight();

    return this.instance.timeRangeStore.records.flatMap((record, index) => {
      const start = toDate(record.getData('startDate'));
      if (!start) {
        return [];
      }
      const end = toDate(record.getData('endDate')) ?? addMinutes(start, 30);

      return expandRecurrence(start, end, record.getData('recurrenceRule'), {
        endDate: windowEnd,
        startDate: windowStart,
      }).map((occurrence, occurrenceIndex) => {
        const from = clamp(occurrence.startDate, windowStart, windowEnd);
        const to = clamp(occurrence.endDate, windowStart, windowEnd);
        return {
          cls: record.getData('cls') ?? '',
          height: Math.max(2, ((to.getTime() - from.getTime()) / MS_PER_HOUR) * hourHeight),
          key: `${record.id ?? index}-${dayStart.toISOString()}-${occurrenceIndex}`,
          name: record.getData('name') ?? '',
          top: ((from.getTime() - windowStart.getTime()) / MS_PER_HOUR) * hourHeight,
        };
      });
    });
  }

  private _buildDayChips(
    day: Date,
    spans: { end: Date; record: CamEventRecord; start: Date }[]
  ): CamCalendarEventBox[] {
    const dayStart = startOfDay(day);
    const dayEnd = addDays(dayStart, 1);

    return spans
      .filter(({ end, start }) => overlaps(start, end, dayStart, dayEnd))
      .map(({ record }) => this._toBox(record, `${record.id ?? record.getName()}-${dayStart.toISOString()}`));
  }

  private _onDragMove = (event: PointerEvent) => {
    const state = this._dragState;
    if (!state) {
      return;
    }
    const deltaMinutes = ((event.clientY - state.pointerY) / this.hourHeight()) * 60;

    if (state.kind === 'resize') {
      const end = snapToMinutes(addMinutes(state.originEnd, deltaMinutes), SNAP_MINUTES);
      const minEnd = addMinutes(state.originStart, MIN_EVENT_MINUTES);
      this._dragPreview.set({
        end: end.getTime() < minEnd.getTime() ? minEnd : end,
        record: state.record,
        start: state.originStart,
      });
      return;
    }

    const duration = state.originEnd.getTime() - state.originStart.getTime();
    const dayDelta = this._dayDeltaFromPointer(event, state.originStart);
    const start = snapToMinutes(addDays(addMinutes(state.originStart, deltaMinutes), dayDelta), SNAP_MINUTES);
    this._dragPreview.set({ end: new Date(start.getTime() + duration), record: state.record, start });
  };

  private _onDragEnd = () => {
    const state = this._dragState;
    const preview = this._dragPreview();
    this._stopDrag();

    if (!state || !preview) {
      return;
    }
    const payload = { eventRecord: state.record, newEndDate: preview.end, newStartDate: preview.start };
    state.record.setData({ endDate: preview.end, startDate: preview.start });
    this.instance.refresh();

    if (state.kind === 'resize') {
      this.beforeDragResizeEnd.emit(payload);
      return;
    }
    this.beforeDragMoveEnd.emit(payload);
  };

  private _stopDrag(): void {
    document.removeEventListener('pointermove', this._onDragMove);
    document.removeEventListener('pointerup', this._onDragEnd);
    this._dragState = null;
    this._dragPreview.set(null);
  }

  private _dayDeltaFromPointer(event: PointerEvent, originStart: Date): number {
    const column = document
      .elementsFromPoint(event.clientX, event.clientY)
      .map(element => (element as HTMLElement).closest<HTMLElement>('.cam-cal__day'))
      .find(Boolean);

    const iso = column?.dataset['date'];
    if (!iso) {
      return 0;
    }
    return Math.round((startOfDay(new Date(iso)).getTime() - startOfDay(originStart).getTime()) / MS_PER_DAY);
  }

  private _setupExternalDrag(feature: CamExternalEventSourceConfig | boolean): void {
    this._externalDrag?.destroy();
    this._externalDrag = null;

    if (!feature) {
      return;
    }
    const config = feature === true ? {} : feature;

    this._externalDrag = new CamDragHelper({
      dragRootElement: config.dragRootElement ?? null,
      dropTargetSelector: '.cam-cal__day',
      onDrop: dropEvent => this._onExternalDrop(dropEvent.source, dropEvent.target, dropEvent.pointerEvent, config),
      targetSelector: config.dragItemSelector ?? '.bucket-item',
    });
  }

  private _onExternalDrop(
    source: HTMLElement,
    target: HTMLElement,
    pointerEvent: PointerEvent,
    config: CamExternalEventSourceConfig
  ): void {
    const iso = target.dataset['date'];
    if (!iso) {
      return;
    }
    const record = config.getRecordFromElement?.(source) ?? this._defaultRecordFromElement(source);
    if (!record) {
      return;
    }

    const dayStart = startOfDay(new Date(iso));
    const offsetMinutes =
      this.dayStartTime() * 60 + ((pointerEvent.clientY - target.getBoundingClientRect().top) / this.hourHeight()) * 60;
    const start = target.dataset['allday']
      ? addMinutes(dayStart, 9 * 60)
      : snapToMinutes(addMinutes(dayStart, offsetMinutes), SNAP_MINUTES);

    record.setDates(start, addUnit(start, record.getDurationUnit(), record.getDuration()));
    this.instance.eventStore.add(record);
  }

  private _defaultRecordFromElement(element: HTMLElement): CamEventRecord {
    const duration = Number(element.dataset['duration']);
    return new CamEventRecord({
      brutJson: element.dataset['brut'],
      duration: isNaN(duration) || !duration ? 1 : duration,
      durationUnit: element.dataset['durationUnit'] ?? 'hour',
      iconCls: element.dataset['iconCls'] ?? '',
      id: element.dataset['id'] ?? '',
      name: element.dataset['name'] ?? '',
    });
  }
}
